import React, { memo, useState, useEffect, useMemo } from "react";
import {
  Stack,
  Icon,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Box,
  Button,
  Menu,
  MenuItem,
} from "investira.react.components";
import { dates, formats } from "investira.sdk";
import { HighchartsReact } from "investira.react.charts";
import { validators } from "investira.sdk";
import services from "../../../../services";
import { CONSOLIDACAO_TIPO } from "@investirapri/financa.js/lib/enums";

const AtivoPatrimonio = memo((props) => {
  const { pessoa } = props;

  console.log("pessoa", pessoa);

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [options, setOptions] = useState([]);
  const [currentOption, setCurrentOptions] = useState(null);
  const [anchorElConsolidaco, setAnchorElConsolidaco] = useState(null);

  const openConsolidacao = Boolean(anchorElConsolidaco);

  const xOptions = useMemo(() => {
    const TODAY = new Date();
    const DATA_FIM = dates.toSqlDate(dates.toSqlDate(TODAY));
    const DATA_INICIO = dates.toSqlDate(
      dates.addYears(dates.toSqlDate(TODAY), -10)
    );

    const xResult = {
      key: "pl",
      request: services.pessoas.pl,
      params: {
        data_inicio: DATA_INICIO,
        data_fim: DATA_FIM,
        pessoa_id: props.pessoa.pessoa_id,
        sort: "data ASC",
        consolidacao_tipo: currentOption,
      },
      formatData: (pData) => {
        return pData.map((xPoint) => {
          const xDate = dates
            .startOf("month", dates.toDate(xPoint.data))
            .getTime();
          return [xDate, xPoint.pl];
        });
      },
      options: {
        title: {
          text: "",
        },
        tooltip: {
          formatter: function (tooltip) {
            const xDate = formats.formatDateCustom(this.point.x, "MMM/YYYY");
            const xValue = formats.friendlyNumber(this.point.y, 2, false);
            const xFullValue = formats.formatNumber(
              this.point.y,
              2,
              true,
              false
            );

            return `
              <span style="font-size: 1em"><b>${xDate}</b></span><br/>
              <span style="color:${this.color}">\u25CF</span>
              ${this.series.name}: <b>${xValue}</b> (${xFullValue})<br/>
              `;
          },
        },
        yAxis: {
          type: "linear",
          title: {
            text:
              pessoa?.setor_id === "65.41.3.00"
                ? `Patrimônio social (R$)`
                : `Patrimônio líquido (R$)`,
          },
        },
        xAxis: {
          type: "datetime",
          crosshair: true,
        },
        series: [
          {
            name: pessoa?.setor_id === "65.41.3.00" ? "PS" : "PL",
            type: "line",
            data: data,
            zones: [
              {
                value: 0, // Up to zero
                color: "#ed442c", // Color for values above zero
              },
              {
                color: "#00dfa8", // Color for values below zero
              },
            ],
          },
        ],
      },
    };

    return xResult;
  }, [data, pessoa, currentOption]);

  function renderConsolidacaoMenu(
    pConsolidacoaTipo,
    pOptions = [],
    pCurrentConsolidacao
  ) {
    if (validators.isEmpty(pOptions)) {
      return null;
    }

    const xMenu = pOptions.map((xId) => {
      const xConsolidacaoTipo = Object.values(pConsolidacoaTipo).filter(
        (xItem) => xItem.id === xId
      )[0];

      return {
        label: xConsolidacaoTipo.descricao,
        value: xConsolidacaoTipo.id,
      };
    });

    const xCurrentLabel = Object.values(pConsolidacoaTipo).filter(
      (xItem) => xItem.id === pCurrentConsolidacao
    )[0];

    const xProps = {
      ...(pOptions.length >= 2
        ? { onClick: (pEvent) => setAnchorElConsolidaco(pEvent.currentTarget) }
        : { disabled: true }),
    };

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          disableElevation
          {...xProps}
          sx={{ fontSize: "0.7rem", padding: "3px 8px" }}
        >
          {xCurrentLabel?.descricao}
        </Button>
        <Menu
          anchorEl={anchorElConsolidaco}
          open={openConsolidacao}
          onClose={() => setAnchorElConsolidaco(null)}
        >
          {xMenu.map((xItem) => {
            return (
              <MenuItem
                key={xItem.value}
                onClick={() => {
                  setCurrentOptions(xItem.value);
                }}
              >
                {xItem.label}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  useEffect(() => {
    setLoading(true);
    props.pessoa.pessoa_id &&
      services.pessoas.pl(
        {
          data: dates.toSqlDate(dates.toDate()),
          pessoa_id: props.pessoa.pessoa_id,
        },
        (rRes) => {
          if (!validators.isEmpty(rRes.data)) {
            let xOptions = new Set();

            rRes.data.forEach((xElem) => {
              xOptions.add(xElem.consolidacao_tipo);
            });

            xOptions = Array.from(xOptions);
            setOptions(xOptions);
            setCurrentOptions(xOptions[1]);
          }
        },
        null,
        () => {
          setLoading(false);
        }
      );
  }, [props.pessoa.pessoa_id]);

  useEffect(() => {
    setLoading(true);
    props.pessoa.pessoa_id &&
      !validators.isNull(currentOption) &&
      xOptions.request({ ...xOptions.params, data: null }, (rRes) => {
        const xData = xOptions.formatData(rRes.data);

        setData(xData);
        setLoading(false);
      });
  }, [props.pessoa.pessoa_id, options, currentOption]);

  // Render
  return (
    <Stack flexGrow={1}>
      <Card>
        <CardContent
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Stack spacing={2} sx={{ py: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="subtitle1"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
              >
                {pessoa?.setor_id === "65.41.3.00"
                  ? "Patrimônio Social"
                  : "Patrimônio líquido"}
              </Typography>
              <Stack alignItems="center" justifyContent="center">
                {renderConsolidacaoMenu(
                  CONSOLIDACAO_TIPO,
                  options,
                  currentOption
                )}
              </Stack>
            </Stack>
            <Stack direction="column" sx={{ gap: 1 }}>
              <HighchartsReact key="pl" options={xOptions.options} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

AtivoPatrimonio.propTypes = {};

export default AtivoPatrimonio;
